import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { ButtonType, LoaderTheme, NotificationTheme } from '@fcom/ui-components';
import {
  capitalize,
  EMAIL_PATTERN,
  isNotBlank,
  isPresent,
  LocalDate,
  PHONE_PATTERN,
  unsubscribe,
} from '@fcom/core/utils';
import { LoginGender, ParsedPhone, Profile, ProfileTier } from '@fcom/core-api/login';
import { FormStatus } from '@fcom/service-forms';
import { trackFormState } from '@fcom/service-forms/services/utils';
import { GtmService } from '@fcom/common/gtm';
import { PhonePrefixService } from '@fcom/common/services/phone-prefix/phone-prefix.service';
import { LoginService } from '@fcom/common/login';
import { SelectOption } from '@fcom/common';

import { PersonalInformationFormFields, PersonalInformationSubmissionData } from '../../interfaces/profile.interface';
import { ProfileService } from '../../services/profile.service';

const PERSONAL_INFORMATION_FORM = 'update-profile-personal-information';

@Component({
  selector: 'fin-loyalty-personal-information-form',
  templateUrl: 'loyalty-personal-information-form.component.html',
  styleUrls: ['./loyalty-personal-information-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class LoyaltyPersonalInfoFormComponent implements OnInit, OnDestroy {
  public readonly FormStatus = FormStatus;
  public readonly PersonalInformationFormFields = PersonalInformationFormFields;
  public readonly ButtonType = ButtonType;
  public readonly NotificationTheme = NotificationTheme;
  public readonly LoaderTheme = LoaderTheme;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  public notification: ElementRef;

  @ViewChild('fcomNotification', { static: false })
  set notificationElem(notificationElem: ElementRef) {
    this.notification = notificationElem;
  }

  reactiveForm: UntypedFormGroup;
  genders = Object.keys(LoginGender).map((gender) => ({ value: LoginGender[gender].toLowerCase() }));
  maxDate: LocalDate = this.maxYear();
  minDate: LocalDate = this.minYear();
  tooltipOptions = {
    enableOnClick: true,
    showCloseButton: true,
    tooltipWidth: 342,
    minOffsetLeft: 10,
  };
  formStatus: FormStatus = FormStatus.INITIAL;
  ProfileTier = ProfileTier;
  dateOfBirth: LocalDate;
  profileStatus: string;
  memberNumber: string;
  profileTier: string;
  name: string;
  changePasswordUrl: string;

  phonePrefixes$: Observable<SelectOption[]>;

  private subscriptions = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private profileService: ProfileService,
    private gtmService: GtmService,
    private phonePrefixService: PhonePrefixService,
    private loginService: LoginService,
    private cdRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  ngOnInit(): void {
    this.formStatus = FormStatus.LOADING;
    this.phonePrefixes$ = this.phonePrefixService.setupPhonePrefixes().pipe(take(1));
    this.subscriptions.add(
      this.profileService.getProfile().subscribe((profile: Profile) => {
        this.createForm(profile);
        this.dateOfBirth = profile.dateOfBirth ? new LocalDate(profile.dateOfBirth) : undefined;
        this.memberNumber = profile.memberNumber;
        this.profileTier = profile.tier;
        this.profileStatus = profile.memberStatus;
        this.formStatus = FormStatus.INITIAL;
        this.name = this.parseName(profile);

        if (isPlatformBrowser(this.platform)) {
          this.changePasswordUrl = this.loginService.createChangePasswordUrl(this.memberNumber);
        }
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  createForm(profile: Profile): void {
    const requiredValidator = profile.tier === ProfileTier.ACCOUNT ? [] : [Validators.required];

    this.reactiveForm = this.fb.group({
      dateOfBirth: [profile.dateOfBirth ? profile.dateOfBirth : '', requiredValidator],
      email: [profile.email ? profile.email : '', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      countryCode: [
        this.parseCountryCode(profile.parsedMobilePhone?.regionCodes, profile.parsedMobilePhone?.countryCode),
        requiredValidator,
      ],
      nationalNumber: [
        profile.parsedMobilePhone?.nationalNumber?.toString() ?? '',
        [...requiredValidator, Validators.pattern(PHONE_PATTERN)],
      ],
      gender: [
        this.genders.find((gender) => gender.value === profile.gender.toLowerCase())
          ? profile.gender.toLowerCase()
          : '',
        requiredValidator,
      ],
    });
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();

      return;
    }

    this.formStatus = FormStatus.LOADING;

    const data: PersonalInformationSubmissionData = {
      email: this.reactiveForm.value[PersonalInformationFormFields.EMAIL],
      phoneNumber: this.parsePhoneNumber(
        this.reactiveForm.value[PersonalInformationFormFields.COUNTRY_CODE],
        this.reactiveForm.value[PersonalInformationFormFields.NATIONAL_NUMBER]
      ),
      gender: capitalize(this.reactiveForm.value[PersonalInformationFormFields.GENDER]),
    };
    if (!this.dateOfBirth && this.reactiveForm.value[PersonalInformationFormFields.DATE_OF_BIRTH]) {
      const date = this.reactiveForm.value[PersonalInformationFormFields.DATE_OF_BIRTH];
      const localDate = new LocalDate(date);
      data.dateOfBirth = localDate.toString();
      this.dateOfBirth = localDate;
    }
    trackFormState(PERSONAL_INFORMATION_FORM, 'send-attempt', this.gtmService);

    const countryCode = this.reactiveForm.value[PersonalInformationFormFields.COUNTRY_CODE];
    const parsedMobilePhone = this.generateParsedMobilePhone(
      isNotBlank(countryCode) ? countryCode.split('|')[1] : '',
      this.reactiveForm.value[PersonalInformationFormFields.NATIONAL_NUMBER],
      isNotBlank(countryCode) ? countryCode.split('|')[0] : ''
    );

    this.subscriptions.add(
      this.profileService.updatePersonalInformation(data, parsedMobilePhone).subscribe({
        next: (res) => {
          if (res.status.message === 'OK') {
            this.formStatus = FormStatus.COMPLETE;
            trackFormState(PERSONAL_INFORMATION_FORM, 'success', this.gtmService);
          } else {
            this.formStatus = FormStatus.ERROR;
            trackFormState(PERSONAL_INFORMATION_FORM, 'error', this.gtmService);
          }
        },
        error: () => {
          this.formStatus = FormStatus.ERROR;
          trackFormState(PERSONAL_INFORMATION_FORM, 'error', this.gtmService);
        },
        complete: () => {
          this.cdRef.detectChanges();
          this.notification.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
      })
    );
  }

  parseName(profile: Profile): string {
    return `${profile.firstname} ${profile.lastname}`;
  }

  parsePhoneNumber(countryCode: string, nationalNumber: string): string {
    return `${countryCode.split('|')[1]}${nationalNumber}`;
  }

  parseCountryCode(regionCodes: string[], countryCode: number): string {
    if (!regionCodes?.length || !countryCode) {
      return '';
    }

    return `${regionCodes[0]}|${countryCode}`;
  }

  minYear(): LocalDate {
    return LocalDate.now().minusYears(100);
  }

  maxYear(): LocalDate {
    return LocalDate.now().minusYears(13);
  }

  private generateParsedMobilePhone(countryCode: string, nationalNumber: string, regionCode: string): ParsedPhone {
    if (isPresent(countryCode) && isNotBlank(countryCode) && isNotBlank(nationalNumber) && isNotBlank(regionCode)) {
      return {
        countryCode: +countryCode,
        nationalNumber: +nationalNumber,
        regionCodes: [regionCode],
      };
    } else {
      return undefined;
    }
  }
}
